import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  MixtureTypeDto,
  MixtureTypeEditDto
} from 'src/store/mixturetypes/mixturetype';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum MixtureTypeTypes {
  FetchAll = 'mixtureTypes/FetchAll',
  Add = 'mixtureTypes/Add',
  Update = 'mixtureTypes/Update',
  Delete = 'mixtureTypes/Delete',
  Failure = 'mixtureTypes/Failure'
}

export type MixtureTypeAction =
  | { type: MixtureTypeTypes.FetchAll; mixtureTypes: MixtureTypeDto[] }
  | { type: MixtureTypeTypes.Add; mixtureType: MixtureTypeDto }
  | { type: MixtureTypeTypes.Update; mixtureType: MixtureTypeDto }
  | { type: MixtureTypeTypes.Delete; mixtureType: MixtureTypeDto }
  | { type: MixtureTypeTypes.Failure; errors: any };

export const actionCreators = {
  fetchMixtureTypes: (mixtureTypes: MixtureTypeDto[]): MixtureTypeAction => ({
    type: MixtureTypeTypes.FetchAll,
    mixtureTypes
  }),
  addMixtureType: (mixtureType: MixtureTypeDto): MixtureTypeAction => ({
    type: MixtureTypeTypes.Add,
    mixtureType
  }),
  updateMixtureType: (mixtureType: MixtureTypeDto): MixtureTypeAction => ({
    type: MixtureTypeTypes.Update,
    mixtureType
  }),
  deleteMixtureType: (mixtureType: MixtureTypeDto): MixtureTypeAction => ({
    type: MixtureTypeTypes.Delete,
    mixtureType
  }),
  mixtureTypeRequestFailure: (errors: any): MixtureTypeAction => ({
    type: MixtureTypeTypes.Failure,
    errors
  })
};

export const fetchMixtureTypes = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(MixtureTypeTypes.FetchAll));
    const { ok, data, error } = await getRequest<MixtureTypeDto[]>(
      '/mixturetypes'
    );
    dispatch(requestEnd(MixtureTypeTypes.FetchAll));

    return ok
      ? dispatch(actionCreators.fetchMixtureTypes(data))
      : dispatch(actionCreators.mixtureTypeRequestFailure(error));
  };
};

export const createMixtureType = (mixtureType: MixtureTypeEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/mixturetypes', mixtureType);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('mixtureType.created')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('create.failed')));
    return dispatch(actionCreators.mixtureTypeRequestFailure(error));
  };
};

export const editMixtureType = (
  id: number,
  mixtureType: MixtureTypeEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/mixturetypes/${id}`, mixtureType);

    if (ok) {
      return dispatch(alertSuccess(i18n.t('mixtureType.updated')));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(actionCreators.mixtureTypeRequestFailure(error));
  };
};

export const deleteMixtureType = (id: number, mixtureType: MixtureTypeDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(
      `/mixtureTypes/${id}`,
      mixtureType
    );

    if (ok) {
      dispatch(alertSuccess(i18n.t('mixtureType.deleted')));
      return dispatch(actionCreators.deleteMixtureType(mixtureType));
    }

    dispatch(alertError(error?.errors?.detail ?? i18n.t('remove.failed')));
    return dispatch(actionCreators.mixtureTypeRequestFailure(error));
  };
};
